import reference_config from "@/components/Reference/config";

/**
 * Libellé d'un entrepôt : code interne (LG…) puis nom complet, sans répéter le code quand
 * le nom le contient déjà (« LG2 EQUANS SOLUTONS MATERIELS CARQUEFOU »).
 */
export const warehouseLabel = (warehouse) => {
  if (!warehouse) {
    return null;
  }
  const code = warehouse.internal_code;
  const name = warehouse.name;
  if (code && name && name.indexOf(code) === 0) {
    return name;
  }
  return [code, name].filter(Boolean).join(" - ") || null;
};

export default {
  storeNamespace: 'business_return',
  authFeature: 'business-return',
  newRouteName: 'BusinessReturnNew',
  listRouteName: "BusinessReturnList",
  editRouteName: "BusinessReturnEdit",
  detailRouteName: "BusinessReturnDetail",
  useNewModal: true,
  useEditModal: true,
  formComponentName: 'BusinessReturnForm',
  csvExportFilenamePrefix: 'demandes-de-retour',
  titleField: 'return_number',
  sortBy: 'return_number',
  sortDesc: true,
  showExtendedFilters: true,
  // L'entrepôt n'est envoyé que s'il a été changé : renvoyer celui d'origine passerait pour
  // un choix manuel et empêcherait le back de le recalculer sur un nouveau code postal.
  submitFormatter: function (form) {
    const { initial_return_warehouse_id, ...data } = form;
    if (data.return_warehouse_id == initial_return_warehouse_id) {
      delete data.return_warehouse_id;
    }
    return data;
  },
  fields: [
    // Liste
    {
      name: 'return_number',
      label: "Numéro",
      type: 'text',
      inList: true,
      sortable: true,
      filterIn: true,
    },
    {
      name: 'axiloc_creation_date',
      label: "Création",
      type: 'date',
      inList: true,
      sortable: true,
      formatter: (context, value) => value ? context.$moment(value).format("DD-MM-YYYY") : null
    },
    {
      name: 'withdraw_wished_date',
      label: "Date de retour (fin de location)",
      type: 'date',
      inList: true,
      sortable: true,
      extendedFilter: true,
      formatter: (context, value) => value ? context.$moment(value).format("DD-MM-YYYY") : null
    },
    {
      name: 'business',
      label: "Affaire",
      type: 'select',
      inList: true,
      sortable: true,
      filterIn: true,
      formatter: (context, value) => value && value.number && value.name ? value.number + ' - ' + value.name : null
    },
    {
      name: 'provenance',
      label: "Provenance",
      type: 'text',
      inList: true,
      sortable: true,
      filterIn: true,
      formatter: (context, value, item) => item && item.withdraw_postal_code && item.withdraw_town ? item.withdraw_town + ' (' + item.withdraw_postal_code.substr(0, 2) + ')' : null
    },
    {
      // Entrepôt de destination : choisi (dépôt), ou déduit du département (Chronopost, enlèvement)
      name: 'return_warehouse',
      label: "Entrepôt",
      type: 'text',
      inList: true,
      formatter: (context, value) => warehouseLabel(value)
    },
    {
      name: 'handling_units_number',
      label: "Nombre d'unités de manutention",
      type: 'number',
      inList: true,
    },
    {
      name: 'tracking_number',
      label: "Numéro de suivi",
      type: 'text',
      inList: true,
      sortable: true,
      filterIn: true,
    },
    {
      // La colonne empilait la liste entière : un retour de vingt matériels étirait la
      // ligne sur autant de hauteurs. Seule la première est affichée, les suivantes sont
      // comptées et détaillées au survol (cf. le slot de BusinessReturnList).
      name: 'unique_references',
      label: "Références uniques",
      inList: true,
      filterIn: true,
      formatter: (context, value) => value && value.length ? {
        first: value[0].internal_code + ' - ' + value[0].product.name,
        others: value.slice(1).map(reference => reference.internal_code + ' - ' + reference.product.name),
      } : null
    },
    {
      name: 'total_weight',
      label: "Poids total",
      inList: true,
      inDetail: true,
      formatter: (context, value, item) => item && item.unique_references ? item.unique_references.reduce((a, b) => a + (b.product.weight || 0), 0) + 'kg' : null
    },
    {
      name: 'status',
      label: "Statut",
      type: 'select',
      inList: true,
      tabFilter: true,
      tabFilterDefaultValue: 'business-return-en-attente',
      key: 'status_slug',
      formatter: (context, value) => value && value.name ? value.name : null
    },
    {
      name: 'group_slugs',
      label: "Services",
      type: 'select',
      inList: true,
      extendedFilter: true,
    },
    {
      // Retour d'échange (RTR…-E) : badge dans la liste (SFD Échanges et stockage 1.1).
      name: 'exchange',
      label: "Type",
      type: 'text',
      inList: true,
      inDetail: false,
      formatter: (context, value) => value ? "Échange" : "Standard",
    },
    {
      name: 'type',
      label: "Type",
      type: 'select',
      extendedFilter: true,
    },
    {
      // INC-003 : filtre entrepôt de retour (select dans "Plus d'options")
      name: 'warehouse',
      label: "Entrepôt",
      type: 'select',
      extendedFilter: true,
    },
    // Form et autre
    {
      name: 'return_number',
      label: "Numéro",
      type: 'text',
      inForm: false,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'agency_number',
      label: "Agence",
      type: 'chosen',
      inForm: true,
      required: true,
      inDetail: false,
      inCsvExport: false,
      inPdfExport: false,
    },
    {
      name: 'agency',
      label: "Agence",
      type: 'select',
      inForm: false,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      formatter: (context, value) => value && value.name ? value.name : null
    },
    {
      name: 'business_number',
      label: "Affaire",
      type: 'chosen',
      inForm: true,
      required: false,
      firstOption: "-- Sélectionnez une agence --",
      inDetail: false,
      inCsvExport: false,
      inPdfExport: false,
    },
    {
      name: 'business',
      label: "Affaire",
      type: 'select',
      inForm: false,
      inDetail: true,
      inPdfExport: true,
      formatter: (context, value) => value && value.number && value.name ? value.number + ' - ' + value.name : null
    },
    {
      name: 'business',
      label: "Affaire",
      inCsvExport: true,
    },
    {
      name: 'user',
      label: "Demandeur",
      inDetail: true,
      inPdfExport: true,
      inCsvExport: true,
      formatter: (context, value) => value && value.firstname && value.lastname ? value.firstname + ' ' + value.lastname : null
    },
    {
      name: 'user_id',
      label: "Demandeur",
      type: 'vueselect',
      inForm: true,
      description: "Utilisateur E-MATERIEL pouvant effectuer un retour. Il s'agit de la liste des utilisateurs."
    },
    {
      name: 'asker_email',
      label: "Email du demandeur",
      type: 'text',
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      formatter: (context, value, item) => item && item.user && item.user.email ? item.user.email : (value ? value : null)
    },
    {
      name: 'asker_number',
      label: "Matricule du demandeur",
      type: 'text',
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      formatter: (context, value, item) => item && item.user && item.user.employee && item.user.employee.number ? item.user.employee.number : (value ? value : null)
    },
    {
      name: 'unique_references',
      label: "Références uniques",
      type: 'group_repeater',
      inForm: true,
      required: true,
      fields: [{
        name: 'id',
        label: "Référence unique",
        type: 'chosen',
        inForm: true,
        required: true,
      }]
    },
    {
      // Chronopost inclus : c'est cette date qui arrête la location pour ce mode
      // (SFD § Arrêt de la Facturation), elle doit donc rester corrigeable ici.
      name: 'withdraw_wished_date',
      label: "Date de retour (fin de location)",
      type: 'date',
      inForm: true,
      required: true,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      formatter: (context, value) => value ? context.$moment(value).format("DD-MM-YYYY") : null
    },
    {
      name: 'material_retrieving_place',
      label: "Le matériel sélectionné doit être récupéré",
      type: 'select',
      inForm: true,
      required: true,
      inDetail: true,
      inCsvExport: true,
    },
    {
      // Entrepôt de destination, corrigeable à la main. Laissé vide, le back le déduit du
      // code postal d'enlèvement (cf. submitFormatter et BusinessReturnManager::addOrEditEntity).
      name: 'return_warehouse_id',
      label: "Entrepôt de retour",
      type: 'select',
      inForm: true,
      required: false,
    },
    {
      name: 'material_retrieving_place',
      label: "Mode de retour",
      type: 'select',
      inList: true,
      inPdfExport: true,
      formatter: (context, value, item) => context.$store.getters['business_return/getMaterialRetrievingPlaceBySlug'](item.material_retrieving_place)
    },
    {
      name: 'withdraw_name',
      label: "Nom du lieu (agence/chantier/autre)",
      type: 'text',
      inForm: true,
      required: true,
      inFormCondition: (form) => form.material_retrieving_place !== 'axiloc' && form.material_retrieving_place !== 'chronopost',
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'withdraw_address',
      label: "Adresse d'enlèvement",
      type: 'text',
      inForm: true,
      required: true,
      inFormCondition: (form) => form.material_retrieving_place !== 'axiloc' && form.material_retrieving_place !== 'chronopost',
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      formatter: (context, value, item) => value && item && item.withdraw_postal_code && item.withdraw_town ? value+' '+item.withdraw_postal_code+' '+item.withdraw_town : (value ? value : null)
    },
    {
      name: 'withdraw_postal_code',
      label: "Code postal",
      type: 'number',
      inForm: true,
      max: 99999,
      required: true,
      inFormCondition: (form) => form.material_retrieving_place !== 'axiloc' && form.material_retrieving_place !== 'chronopost',
      inCsvExport: true,
    },
    {
      name: 'withdraw_town',
      label: "Ville",
      type: 'text',
      inForm: true,
      required: true,
      inFormCondition: (form) => form.material_retrieving_place !== 'axiloc' && form.material_retrieving_place !== 'chronopost',
      inCsvExport: true,
    },
    {
      name: 'first_contact_name',
      label: "Nom du premier contact",
      type: 'text',
      inForm: true,
      required: true,
      inFormCondition: (form) => form.material_retrieving_place !== 'axiloc' && form.material_retrieving_place !== 'chronopost',
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'first_contact_phone',
      label: "Téléphone du premier contact",
      type: 'text',
      inForm: true,
      required: true,
      inFormCondition: (form) => form.material_retrieving_place !== 'axiloc' && form.material_retrieving_place !== 'chronopost',
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'second_contact_name',
      label: "Nom du second contact",
      type: 'text',
      inForm: true,
      required: false,
      inFormCondition: (form) => form.material_retrieving_place !== 'axiloc' && form.material_retrieving_place !== 'chronopost',
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'second_contact_phone',
      label: "Téléphone du second contact",
      type: 'text',
      inForm: true,
      required: false,
      inFormCondition: (form) => form.material_retrieving_place !== 'axiloc' && form.material_retrieving_place !== 'chronopost',
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'required_truck_tailgate',
      label: "Camion à hayon requis",
      type: 'yesno',
      inForm: true,
      required: true,
      inFormCondition: (form) => form.material_retrieving_place !== 'axiloc' && form.material_retrieving_place !== 'chronopost',
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'reachable_by_semitrailer_truck',
      label: "Accessible en semi-remorque",
      type: 'yesno',
      inForm: true,
      required: true,
      inFormCondition: (form) => form.material_retrieving_place !== 'axiloc' && form.material_retrieving_place !== 'chronopost',
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'handling_units_number',
      label: "Nombre d'unités de manutention",
      type: 'number',
      inForm: true,
      required: true,
      inFormCondition: (form) => form.material_retrieving_place !== 'axiloc',
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'tracking_number',
      label: "Numéro de suivi",
      type: 'text',
      inForm: true,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'comment',
      label: "Merci de décrire précisément votre conditionnement",
      type: 'textarea',
      inForm: true,
      required: false,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'failure_warning_comment',
      label: "Merci de nous signaler si l'un ou plusieurs de ces matériels sont en panne",
      type: 'textarea',
      inForm: true,
      required: false,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
    },
    {
      name: 'total_weight',
      label: "Poids total",
      inCsvExport: true,
      formatter: (context, value, item) => value ? value.replace("kg", "") : null
    },
    {
      name: 'status_slug',
      label: "Statut",
      type: 'select',
      inForm: true,
      required: true,
      inDetail: false,
      inCsvExport: false,
      inPdfExport: false,
    },
    {
      name: 'status',
      label: "Statut",
      type: 'select',
      inForm: false,
      inDetail: true,
      inPdfExport: true,
      formatter: (context, value) => value && value.name ? value.name : null
    },
    {
      name: 'status',
      label: "Statut",
      inCsvExport: true,
    },
    {
      name: 'axiloc_creation_date',
      label: "Création",
      type: 'date',
      inForm: false,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      formatter: (context, value) => value ? context.$moment(value).format("DD-MM-YYYY") : null
    },
    {
      name: 'updated_at',
      label: "Dernière modification",
      type: 'date',
      inForm: false,
      inDetail: true,
      inCsvExport: true,
      inPdfExport: true,
      formatter: (context, value) => value ? context.$moment(value).format("DD-MM-YYYY") : null
    },
    {
      name: 'unique_references',
      label: "Références uniques",
      type: 'list',
      config: reference_config,
      fields: ['product', 'internal_code', 'status']
    },
    {
      name: 'stored_files',
      label: "Fichiers joints",
      type: 'group_repeater',
      inForm: true,
      fields: [{
        name: 'file',
        label: "Fichier",
        type: 'file',
        inForm: true,
      }]
    },
  ]
}



// WEBPACK FOOTER //
// ./src/components/BusinessReturn/config.js